// pipe: 4B.8 (Task 12) — the PURE, prop-only driver pickup-execution view (the
// Vitest-tested seam). The driver completes a pickup here: review declared
// materials, capture the actual net weight, draw the three §3.2.43 signatures
// (releasing/customer + agent/driver + logistics), consent to GPS, then Complete.
// UI-polish: rendered inside the shared <DriverScreen> frame; the two CTAs are the
// @affine/component Button; the primary "Complete" lives in a sticky bottom action
// bar so it stays reachable past the long signature pads.
//
// Import discipline (mirrors pickups-view.tsx): this file imports ONLY
// @affine/component, @affine/i18n, the shared driver-screen frame + css modules,
// the local SignaturePad leaf, the pure pickup-body helper (asNumber), and
// TYPE-ONLY service types (erased by swc). It must NOT pull the runtime
// DriverErpService, the auth/PKCE plumbing, react-router, or any DI. The container
// (pickup-exec.tsx) owns ALL of that — fetch, state, the geolocation consent call,
// the PickupCompleteBody construction, and navigation.
//
// THE LOAD-BEARING LOGIC (the brief): "Complete" is DISABLED until the three
// signatures are captured AND GPS is RESOLVED (granted→coords OR denied — a
// denial does NOT block: GPS is exceeds-standard, the pickup still completes); a
// net weight that DEVIATES from the order's declared weight REQUIRES an override
// reason. The container sets the body `geo` only when coords were obtained and
// `net_weight_override_reason` only when the weight deviates — this view exposes
// the same `deviates`/`gpsResolved` predicates so the affordance matches.
// Subpath imports (the package exports map supports them) so the lean driver
// bundle pulls only these modules, never the whole @affine/component barrel.
import { Button } from '@affine/component/ui/button';
import { Input } from '@affine/component/ui/input';
import { Loading } from '@affine/component/ui/loading';
import { useI18n } from '@affine/i18n';
import { PinIcon } from '@blocksuite/icons/rc';
import { useCallback } from 'react';

import type { Geo, PickupDetail } from '../services/driver-erp';
import { DriverScreen } from './driver-screen';
import * as ui from './driver-ui.css';
import { asNumber } from './pickup-body';
import * as styles from './pickup-exec.css';
import { SignaturePad } from './signature-pad';

/**
 * The GPS consent state for one pickup attempt:
 *  - `'pending'` — not yet resolved (the driver hasn't pressed "Capture GPS", or
 *    the browser hasn't answered). Blocks Complete.
 *  - a {@link Geo} coords object — consent granted, position obtained. The
 *    container sets the body `geo` from this.
 *  - `'denied'` — consent denied or position unavailable, acknowledged. Does NOT
 *    block (GPS is exceeds-standard); the body omits `geo`.
 */
export type GpsState = 'pending' | 'denied' | Geo;

export interface PickupExecViewProps {
  detail: PickupDetail | null;
  loading?: boolean;
  error?: string | null;
  /** Navigate back to the pickup list (the container owns the route). */
  onBack?: () => void;
  // The three §3.2.43 signatures (PNG data-URLs, '' when not yet captured).
  releasingSig: string;
  agentSig: string;
  logisticsSig: string;
  onSig: (role: 'releasing' | 'agent' | 'logistics', dataUrl: string) => void;
  // The licensed driver acting (the #5 guard requires a licensed driver).
  agentEmployeeId: string;
  onAgentEmployeeId: (v: string) => void;
  // The captured actual net weight (string-bound input; parsed by the container).
  netWeight: string;
  onNetWeight: (v: string) => void;
  overrideReason: string;
  onOverrideReason: (v: string) => void;
  gpsState: GpsState;
  onCaptureGps: () => void;
  onComplete: () => void;
  submitting?: boolean;
  submitError?: string | null;
}

// A short, human-scannable child label (children carry a redacted projection).
function childLabel(child: Record<string, unknown>): string {
  const title = child.title;
  if (typeof title === 'string' && title.trim() !== '') {
    return title;
  }
  const id = child.id;
  return typeof id === 'string' ? id.slice(0, 8) : '—';
}

export function PickupExecView(props: PickupExecViewProps) {
  const t = useI18n();

  // Stable per-role signature handlers (keyed on the container's stable `onSig`)
  // so the memoized SignaturePads skip re-rendering when unrelated view state
  // changes — a keystroke in the weight/reason/agent-id inputs (whose handlers
  // are already the stable `props.on*` setters) re-renders only what changed.
  // Declared BEFORE the early returns (hook order).
  const { onSig } = props;
  const onReleasingSig = useCallback(
    (d: string) => onSig('releasing', d),
    [onSig]
  );
  const onAgentSig = useCallback((d: string) => onSig('agent', d), [onSig]);
  const onLogisticsSig = useCallback(
    (d: string) => onSig('logistics', d),
    [onSig]
  );

  if (props.error || (!props.detail && !props.loading)) {
    return (
      <DriverScreen onBack={props.onBack}>
        <div className={ui.errorPanel} role="alert">
          {t['com.affine.pipe.driver.exec.error']()}
        </div>
      </DriverScreen>
    );
  }

  if (!props.detail) {
    return (
      <DriverScreen onBack={props.onBack}>
        <div className={ui.statusPanel} role="status">
          <Loading size={24} />
          {t['com.affine.pipe.driver.exec.loading']()}
        </div>
      </DriverScreen>
    );
  }

  const { detail } = props;
  const declared = asNumber(detail.order.data.declared_weight);
  const net = asNumber(props.netWeight);

  // A deviation exists only when BOTH the declared and entered weights are known
  // and differ — mirrors the container's body construction (the override reason
  // is sent only when this is true). An absent declared weight cannot deviate.
  const deviates =
    net != null && declared != null && Math.abs(net - declared) > 0;

  const gpsResolved = props.gpsState !== 'pending';
  const haveSigs = !!(
    props.releasingSig &&
    props.agentSig &&
    props.logisticsSig
  );
  const reasonMissing = deviates && !props.overrideReason.trim();
  // The #5 server guard needs a licensed driver: a blank/whitespace id serializes
  // to `agent_employee_id: ''` and the server fails Uuid deserialization with a
  // generic 400. Gate the affordance until a non-empty id is entered.
  const agentMissing = !props.agentEmployeeId.trim();

  const completeDisabled =
    !!props.submitting ||
    !(haveSigs && gpsResolved) ||
    reasonMissing ||
    agentMissing;

  // A single, specific hint for whatever is currently blocking Complete. Shown
  // only when the action is disabled (and not while a submission is in flight).
  let blockHint: string | null = null;
  if (!props.submitting) {
    if (agentMissing) {
      blockHint = t['com.affine.pipe.driver.exec.agent-required']();
    } else if (!haveSigs) {
      blockHint = t['com.affine.pipe.driver.exec.need-signatures']();
    } else if (!gpsResolved) {
      blockHint = t['com.affine.pipe.driver.exec.gps.pending']();
    } else if (reasonMissing) {
      blockHint = t['com.affine.pipe.driver.exec.override-required']();
    }
  }

  const gpsStatus =
    props.gpsState === 'pending'
      ? null
      : props.gpsState === 'denied'
        ? t['com.affine.pipe.driver.exec.gps.denied']()
        : t['com.affine.pipe.driver.exec.gps.captured']();

  return (
    <DriverScreen
      title={t['com.affine.pipe.driver.exec.title']()}
      onBack={props.onBack}
    >
      {/* --- Declared vs. actual weight ------------------------------------ */}
      <section className={styles.section}>
        <div className={styles.row}>
          <span className={styles.fieldLabel}>
            {t['com.affine.pipe.driver.exec.declared-weight']()}
          </span>
          <span className={styles.declaredValue}>
            {declared != null ? declared : '—'}
          </span>
        </div>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>
            {t['com.affine.pipe.driver.exec.net-weight']()}
          </span>
          <Input
            type="number"
            inputMode="decimal"
            value={props.netWeight}
            onChange={props.onNetWeight}
          />
        </label>
        {deviates ? (
          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              {t['com.affine.pipe.driver.exec.override-reason']()}
            </span>
            <Input
              value={props.overrideReason}
              onChange={props.onOverrideReason}
            />
          </label>
        ) : null}
      </section>

      {/* --- Declared materials (children) -------------------------------- */}
      {detail.children.length > 0 ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            {t['com.affine.pipe.driver.exec.materials']()}
          </h2>
          <ul className={styles.childList}>
            {detail.children.map((child, i) => (
              <li
                key={typeof child.id === 'string' ? child.id : i}
                className={styles.childRow}
              >
                {childLabel(child)}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* --- Acting driver ------------------------------------------------- */}
      <section className={styles.section}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>
            {t['com.affine.pipe.driver.exec.agent-employee']()}
          </span>
          <Input
            value={props.agentEmployeeId}
            onChange={props.onAgentEmployeeId}
          />
        </label>
      </section>

      {/* --- The three §3.2.43 signatures --------------------------------- */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          {t['com.affine.pipe.driver.exec.signatures']()}
        </h2>
        <SignaturePad
          label={t['com.affine.pipe.driver.exec.sig.releasing']()}
          value={props.releasingSig}
          onChange={onReleasingSig}
        />
        <SignaturePad
          label={t['com.affine.pipe.driver.exec.sig.agent']()}
          value={props.agentSig}
          onChange={onAgentSig}
        />
        <SignaturePad
          label={t['com.affine.pipe.driver.exec.sig.logistics']()}
          value={props.logisticsSig}
          onChange={onLogisticsSig}
        />
      </section>

      {/* --- GPS consent --------------------------------------------------- */}
      <section className={styles.section}>
        <div className={styles.row}>
          <Button
            variant="secondary"
            size="large"
            prefix={<PinIcon />}
            onClick={props.onCaptureGps}
          >
            {t['com.affine.pipe.driver.exec.gps.capture']()}
          </Button>
          {gpsStatus ? (
            <span className={styles.gpsStatus}>{gpsStatus}</span>
          ) : null}
        </div>
      </section>

      {/* --- Sticky Complete action bar ----------------------------------- */}
      <div className={styles.actionBar}>
        {props.submitError ? (
          <div className={styles.submitError} role="alert">
            {t['com.affine.pipe.driver.exec.submit-error']()}
          </div>
        ) : null}
        {blockHint ? <div className={styles.blockHint}>{blockHint}</div> : null}
        <Button
          variant="primary"
          size="large"
          block
          loading={props.submitting}
          disabled={completeDisabled}
          onClick={props.onComplete}
        >
          {t['com.affine.pipe.driver.exec.complete']()}
        </Button>
      </div>
    </DriverScreen>
  );
}
